'use client'

import { useEffect, useRef } from 'react'
import type { FC } from 'react'
import Image from 'next/image'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { ArrowRight } from 'lucide-react'
import gsap from 'gsap'

export interface PsychologistCardProps {
  member: {
    name: string
    title: string
    credentials?: string[] | string
    imageUrl: string
    specialty: string
    yearsExperience: number
    expertise: string[]
  }
  onViewBio: () => void
  imageHeightClass?: string
  imageFit?: 'cover' | 'contain'
  imageBackgroundClassName?: string
}

const PsychologistCard: FC<PsychologistCardProps> = ({
  member,
  onViewBio,
  imageHeightClass,
  imageFit,
  imageBackgroundClassName,
}) => {
  const cardRef = useRef<HTMLDivElement | null>(null)
  const imageRef = useRef<HTMLDivElement | null>(null)
  const barRef = useRef<HTMLDivElement | null>(null)
  const borderRef = useRef<HTMLDivElement | null>(null)
  const borderTl = useRef<gsap.core.Tween | null>(null)
  const iconRef = useRef<SVGSVGElement | null>(null)
  const buttonRef = useRef<HTMLButtonElement | null>(null)
  const btnTextTopRef = useRef<HTMLSpanElement | null>(null)
  const btnTextBottomRef = useRef<HTMLSpanElement | null>(null)

  const hoverTl = useRef<gsap.core.Timeline | null>(null)
  const leaveTl = useRef<gsap.core.Timeline | null>(null)

  useEffect(() => {
    const cardEl = cardRef.current
    if (!cardEl) return

    const prefersReduced = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches
    const isCoarse = window.matchMedia('(pointer: coarse)').matches

    // Skip all hover animations on touch devices or reduced motion
    if (prefersReduced || isCoarse) return

    hoverTl.current = gsap.timeline({
      paused: true,
      defaults: { ease: 'power2.out' },
    })

    hoverTl.current
      .to(cardEl, { y: -6, duration: 0.25, ease: 'power3.out' }, 0)
      .to(
        imageRef.current,
        { scale: 1.05, duration: 0.45, ease: 'power3.out' },
        0,
      )
      .to(
        barRef.current,
        { backgroundColor: 'rgba(0,0,0,0.42)', duration: 0.35 },
        0,
      )
      .to(
        buttonRef.current,
        {
          scale: 1.05,
          backgroundColor: '#67E8F9',
          color: '#0a0a0a',
          duration: 0.25,
        },
        0,
      )
      .to(iconRef.current, { x: 4, duration: 0.25 }, 0)

    if (btnTextTopRef.current && btnTextBottomRef.current) {
      hoverTl.current
        .set(
          btnTextBottomRef.current,
          {
            opacity: 0,
            rotateX: -90,
            transformOrigin: 'bottom center',
            perspective: 400,
          },
          0,
        )
        .to(
          btnTextTopRef.current,
          {
            opacity: 0,
            rotateX: 90,
            transformOrigin: 'top center',
            perspective: 400,
            duration: 0.35,
          },
          0,
        )
        .to(
          btnTextBottomRef.current,
          { opacity: 1, rotateX: 0, duration: 0.5, ease: 'power3.out' },
          0.12,
        )
    }

    leaveTl.current = gsap.timeline({
      paused: true,
      defaults: { ease: 'power2.out' },
    })

    leaveTl.current
      .to(cardEl, { y: 0, duration: 0.2 }, 0)
      .to(imageRef.current, { scale: 1, duration: 0.4 }, 0)
      .to(
        barRef.current,
        { backgroundColor: 'rgba(0,0,0,0.36)', duration: 0.35 },
        0,
      )
      .to(
        buttonRef.current,
        {
          scale: 1,
          backgroundColor: '#ffffff',
          color: '#0a0a0a',
          duration: 0.25,
        },
        0,
      )
      .to(iconRef.current, { x: 0, duration: 0.25 }, 0)

    if (btnTextTopRef.current && btnTextBottomRef.current) {
      leaveTl.current
        .to(
          btnTextBottomRef.current,
          {
            opacity: 0,
            rotateX: -90,
            transformOrigin: 'bottom center',
            perspective: 400,
            duration: 0.3,
            ease: 'power2.in',
          },
          0,
        )
        .to(
          btnTextTopRef.current,
          {
            opacity: 1,
            rotateX: 0,
            transformOrigin: 'top center',
            perspective: 400,
            duration: 0.4,
            ease: 'power3.out',
          },
          0.08,
        )
    }

    const onEnter = () => {
      leaveTl.current?.pause()
      hoverTl.current?.restart()

      if (borderRef.current) {
        borderTl.current = gsap.to(borderRef.current, {
          duration: 6,
          repeat: -1,
          ease: 'none',
          css: { '--angle': '+=360deg' as any },
        })
      }
    }

    const onLeave = () => {
      hoverTl.current?.pause()
      leaveTl.current?.restart()

      if (borderTl.current) {
        borderTl.current.kill()
        borderTl.current = null
        if (borderRef.current) {
          gsap.set(borderRef.current, { css: { '--angle': '0deg' as any } })
        }
      }
    }

    cardEl.addEventListener('mouseenter', onEnter)
    cardEl.addEventListener('mouseleave', onLeave)

    return () => {
      cardEl.removeEventListener('mouseenter', onEnter)
      cardEl.removeEventListener('mouseleave', onLeave)
      hoverTl.current?.kill()
      leaveTl.current?.kill()
      borderTl.current?.kill()
      hoverTl.current = null
      leaveTl.current = null
      borderTl.current = null
    }
  }, [])

  return (
    <Card
      ref={cardRef}
      className="overflow-hidden shadow-lg cursor-pointer rounded-2xl sm:rounded-[32px] relative will-change-transform"
      onClick={onViewBio}
      tabIndex={0}
      role="button"
      aria-label={`View profile for ${member.name}`}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onViewBio()
        }
      }}
    >
      {/* Animated gradient border overlay */}
      <div
        ref={borderRef}
        className="pointer-events-none absolute inset-0 rounded-2xl sm:rounded-[32px]"
        style={
          {
            background:
              'conic-gradient(from var(--angle, 0deg), rgba(99,236,255,0.85) 0%, rgba(99,236,255,0.2) 20%, rgba(99,236,255,0.85) 40%, rgba(99,236,255,0.2) 60%, rgba(99,236,255,0.85) 80%, rgba(99,236,255,0.2) 100%)',
            WebkitMask:
              'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
            WebkitMaskComposite: 'xor',
            maskComposite: 'exclude',
            padding: '2px',
            borderRadius: 'inherit',
            boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.08)',
            opacity: 1,
            zIndex: 30,
          } as React.CSSProperties
        }
      />

      {/* Background Image */}
      <div
        ref={imageRef}
        className={`relative w-full ${imageHeightClass ?? 'h-[20rem] sm:h-[22rem] md:h-[24rem]'} will-change-transform ${
          imageBackgroundClassName ?? ''
        }`}
      >
        <Image
          src={member.imageUrl}
          alt={`${member.name}, ${member.title}`}
          fill
          sizes="(max-width: 639px) 100vw, (max-width: 1023px) 50vw, 33vw"
          className={imageFit === 'contain' ? 'object-contain' : 'object-cover'}
          priority={false}
        />
      </div>

      {/* Bottom gradient for text contrast */}
      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/50 via-black/20 to-transparent h-28 sm:h-36 rounded-b-2xl sm:rounded-b-[32px]" />

      {/* Frosted glass info bar */}
      <div className="absolute inset-x-0 bottom-0">
        <div
          ref={barRef}
          className="relative flex items-center justify-between w-full px-4 sm:px-7 py-4 sm:py-5 min-h-[96px] sm:min-h-[116px] bg-[rgba(0,0,0,0.36)] backdrop-blur-2xl backdrop-saturate-150 ring-1 ring-white/15 rounded-b-2xl sm:rounded-b-[32px] shadow-[0_8px_24px_rgba(0,0,0,0.35)] overflow-hidden"
        >
          {/* Inner highlight */}
          <div className="pointer-events-none absolute inset-0 rounded-b-2xl sm:rounded-b-[32px] shadow-[inset_0_1px_0_rgba(255,255,255,0.2)]" />

          {/* Name and Title */}
          <div className="relative z-10 text-white max-w-[58%] sm:max-w-[62%] md:max-w-[70%]">
            <h3 className="text-[14px] sm:text-[16px] md:text-[20px] font-bold leading-snug tracking-[-0.01em] line-clamp-2 mb-0.5 sm:mb-1">
              {member.name}
            </h3>
            <p className="text-[13px] sm:text-[16px] md:text-[19px] font-normal text-white/90 leading-snug">
              {member.title}
            </p>
          </div>

          {/* See Profile Button - two spans for 3D flip animation on hover */}
          <Button
            ref={buttonRef}
            className="relative z-10 bg-white text-black rounded-full px-3 sm:px-5 py-2 sm:py-[10px] h-auto min-h-[34px] sm:min-h-[37px] font-medium text-[13px] sm:text-[16px] tracking-[-0.01em] flex items-center gap-1.5 sm:gap-2 shadow overflow-hidden"
            onClick={(e) => {
              e.stopPropagation()
              onViewBio()
            }}
          >
            {/* Default visible text - flips up on hover */}
            <span ref={btnTextTopRef} className="relative block">
              See Profile
            </span>
            {/* Hidden duplicate - flips in from below on hover */}
            <span
              ref={btnTextBottomRef}
              className="absolute left-3 sm:left-5"
              aria-hidden="true"
            >
              See Profile
            </span>
            <ArrowRight ref={iconRef} className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
          </Button>
        </div>
      </div>

      {/* Inset glow */}
      <div className="absolute inset-0 pointer-events-none shadow-[inset_0_2px_6px_rgba(255,255,255,0.10)] rounded-2xl sm:rounded-[32px]" />
    </Card>
  )
}

export default PsychologistCard
